'use client';

import React from 'react';
import styled, { css } from 'styled-components';

export interface PlateProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Render in a muted/inactive state — softer outline, faint ink. */
  dim?: boolean;
  children?: React.ReactNode;
}

const Chip = styled.span<{ $dim: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: ${(p) => p.theme.radius.sm};
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  ${(p) =>
    p.$dim
      ? css`
          background: ${p.theme.colors.neutral.bg};
          border: 1px solid ${p.theme.colors.neutral.line};
          color: ${p.theme.colors.neutral.inkMute};
        `
      : css`
          background: ${p.theme.colors.neutral.panel};
          border: 1px solid ${p.theme.colors.neutral.lineStrong};
          color: ${p.theme.colors.neutral.ink};
        `}
`;

/**
 * Mono identifier chip with a hairline outline. Used for vehicle plates,
 * driver IDs, scenario slugs, and any short uppercase identifier that
 * needs to scan vertically when stacked. The `dim` variant softens the
 * chip when the entity is inactive or out of focus.
 */
export function Plate({ dim = false, children, ...rest }: PlateProps) {
  return (
    <Chip $dim={dim} {...rest}>
      {children}
    </Chip>
  );
}
